import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Tab,
  TabGroup,
  TabList,
  TabPanels,
  Transition,
} from "@headlessui/react";
import clsx from "clsx";
import React from "react";

import type { IconsType } from "@web-app/components";
import { Button, DateTimeTz, Icon } from "@web-app/components";
import { AppFilterType } from "@web-app/types";

import AssigneesFilterTab from "./AssigneesFilterTab";
import ConnectionsFilterTab from "./ConnectionsFilterTab";
import DateTimeFilterTab from "./DateTimeFilterTab";
import DeliveryTypeFilterTab from "./DeliveryTypeFilterTab";
import Pills from "./Pills";
import StatusFilterTab from "./StatusFilterTab";
import useOrdersFilterDropdown from "./useOrdersFilterDropdown";

export default function OrdersFilterDropdown({
  appFilter,
  isDateRange,
  setAppFilter,
}: {
  appFilter: AppFilterType;
  isDateRange?: boolean;
  setAppFilter: (value: AppFilterType) => void;
}) {
  const {
    connections,
    connectionCount,
    statusCount,
    deliveryTypeCount,
    assigneeCount,
    temporaryAppFilter,
    setTemporaryAppFilter,
    formattedText,
    popoverFilterButtonRef,
    tabgroupDefaultIndex,
    assigneeSearch,
    setAssigneeSearch,
    openAppFilterPopover,
    onChange,
    onReset,
    onApply,
    onChangeOrderStatus,
    onChangeDriverStatus,
    onChangeDeliveryType,
    onChangeConnection,
  } = useOrdersFilterDropdown({ appFilter, setAppFilter });

  const tabItemWrapperClass = (extra?: string) =>
    clsx(
      "font-semibold flex text-[10px] lg:text-xs items-center gap-2 lg:mb-1",
      extra,
    );

  return (
    <div className="relative flex items-center lg:items-stretch gap-2">
      <span className="hidden 2xl:flex">
        <Pills
          statusCount={statusCount}
          deliveryTypeCount={deliveryTypeCount}
          assigneeCount={assigneeCount}
          connectionCount={connectionCount}
          formattedText={formattedText}
          isDateRange={isDateRange}
          appFilter={appFilter}
          temporaryAppFilter={temporaryAppFilter}
          openAppFilterPopover={openAppFilterPopover}
          onChange={onChange}
        />
      </span>
      <Popover>
        <PopoverButton
          ref={popoverFilterButtonRef}
          className={clsx(
            "flex items-center justify-center bg-white rounded-md lg:h-full",
            "font-semibold text-sm ",
            "hover:bg-gray-800 hover:text-white focus:outline-none ",
            "transition ease-in ",
            "p-[5px] md:px-[6px] md:py-[8px] 2xl:py-[5px]",
          )}
        >
          <Icon name="filter" />
        </PopoverButton>
        <Transition
          as="div"
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
          className="absolute -right-11 lg:right-0 top-[45px] z-[60] w-[350px] lg:w-[470px] rounded-md bg-white shadow-xl"
        >
          <PopoverPanel>
            <>
              <TabGroup
                defaultIndex={tabgroupDefaultIndex || 0}
                className="flex flex-col lg:flex-row"
              >
                <TabList className="flex w-auto flex-wrap max-w-full relative lg:static overflow-x-scroll lg:overflow-x-auto lg:w-[250px] lg:flex-col gap-1 lg:gap-2 rounded-tl-md rounded-tr-md lg:rounded-l-md border-gray-200 bg-gray-800 p-1 pt-2 lg:pt-2">
                  <TabItem>
                    <span className={tabItemWrapperClass()}>
                      <TabIcon name="fasCalendarDays" />
                      <span>Date & Time</span>
                    </span>
                    <span className="hidden lg:block">
                      <DateTimeTz
                        presentation="filter"
                        start={
                          new Date(temporaryAppFilter.scheduledDateTimeStart)
                        }
                        end={new Date(temporaryAppFilter.scheduledDateTimeEnd)}
                        isDateRange={isDateRange}
                      />
                    </span>
                  </TabItem>
                  <TabItem>
                    <span className={tabItemWrapperClass()}>
                      <TabIcon name="fasCheck" />
                      Status{" "}
                      {!!(
                        temporaryAppFilter.orderStatus.length ||
                        temporaryAppFilter.driverStatus.length
                      ) &&
                        `(${
                          temporaryAppFilter.orderStatus.length +
                          temporaryAppFilter.driverStatus.length
                        })`}
                    </span>
                  </TabItem>
                  <TabItem>
                    <span className={tabItemWrapperClass()}>
                      <TabIcon name="boxesPacking" />
                      Delivery Type{" "}
                      {!!temporaryAppFilter.orderDeliveryType.length &&
                        `(${temporaryAppFilter.orderDeliveryType.length})`}
                    </span>
                  </TabItem>
                  <TabItem>
                    <span className={tabItemWrapperClass()}>
                      <TabIcon name="fasUser" />
                      Assignee{" "}
                      {!!temporaryAppFilter.assigneeIds.length &&
                        `(${temporaryAppFilter.assigneeIds.length})`}
                    </span>
                  </TabItem>
                  <TabItem>
                    <span className={tabItemWrapperClass()}>
                      <TabIcon name="fasGlobe" />
                      Connection{" "}
                      {!!temporaryAppFilter.workspaceIds.length &&
                        `(${temporaryAppFilter.workspaceIds.length})`}
                    </span>
                  </TabItem>
                </TabList>
                <TabPanels className="relative h-[360px] w-full py-2 ">
                  <DateTimeFilterTab
                    isDateRange={isDateRange}
                    temporaryAppFilter={temporaryAppFilter}
                    setTemporaryAppFilter={setTemporaryAppFilter}
                    onChange={onChange}
                  />
                  <StatusFilterTab
                    temporaryAppFilter={temporaryAppFilter}
                    onChangeOrderStatus={onChangeOrderStatus}
                    onChangeDriverStatus={onChangeDriverStatus}
                  />
                  <DeliveryTypeFilterTab
                    temporaryAppFilter={temporaryAppFilter}
                    onChangeDeliveryType={onChangeDeliveryType}
                  />
                  <AssigneesFilterTab
                    temporaryAppFilter={temporaryAppFilter}
                    assigneeSearch={assigneeSearch}
                    setAssigneeSearch={setAssigneeSearch}
                    onChange={onChange}
                  />
                  <ConnectionsFilterTab
                    temporaryAppFilter={temporaryAppFilter}
                    onChangeConnection={onChangeConnection}
                    connections={connections || []}
                  />
                  <div className="absolute bottom-0 mt-5 flex w-full justify-between border-t bg-white p-2 ">
                    <Button
                      plain
                      size="sm"
                      onClick={onReset}
                      data-testid="filter-reset-button"
                    >
                      Reset
                    </Button>
                    <Button
                      onClick={onApply}
                      size="sm"
                      data-testid="filter-apply-button"
                    >
                      Apply
                    </Button>
                  </div>
                </TabPanels>
              </TabGroup>
            </>
          </PopoverPanel>
        </Transition>
      </Popover>
    </div>
  );
}

function TabItem({ children }: { children: React.ReactNode }) {
  return (
    <Tab className="focus:outline-none w-[100px] lg:w-auto ">
      {({ selected }) => (
        <div
          className={clsx(
            "mx-2 flex justify-center lg:justify-start items-center lg:items-start flex-col rounded-md p-[3px] lg:p-2 lg:pl-3 text-left text-xs transition ease-in hover:bg-gray-100 hover:text-gray-700 focus:outline-none",
            selected ? "bg-gray-100" : "text-gray-200",
          )}
        >
          {children}
        </div>
      )}
    </Tab>
  );
}

function TabIcon({ name }: { name: IconsType }) {
  return <Icon name={name} className="hidden lg:visible" />;
}
